import React, {useState} from 'react';
import {useLocation} from "react-router-dom";
import {useForm} from "react-hook-form";
import {useSystemMessage} from "../hooks/useSystemMessage";
import useAppRouter from "../hooks/useAppRouter";
import {userApi} from "../apis/user/userApi";
import emailIcon from "../assets/images/email_icon.png";
import useLoading from "../hooks/useLoading";
import {FormValidationType, makeRegisterOptions} from "../libs/reactHookForm/makeRegisterOptions";
import {ValidationErrorMessages} from "../components/pages/common/ValidationErrorMessages";
import {ApiClientError} from "../apis/errors";
import {logError} from "../libs/utils/logError";

export interface PasswordResetFormValues {
  email: string;
}

export const PasswordResetMailSendPage: React.FC = () => {
  const {success} = useSystemMessage();
  const [abortController, setAbortController] = useState<AbortController | undefined>();
  const [ apiClientError, setApiClientError ] = useState<ApiClientError | null>(null);
  const { goto } = useAppRouter();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    reValidateMode: 'onSubmit',
  });
  const withLoading = useLoading();
  const location = useLocation();

  const handleSend = async () => {
    withLoading(async () => {
      setApiClientError(null);
      await handleSubmit(async (data: PasswordResetFormValues) => {
        setAbortController(new AbortController());
        try {
          await userApi.sendPasswordResetMail({
            email: data.email
          }, {abortSignal: abortController?.signal});
          setAbortController(undefined);
          await success('パスワードリセット用のメールを送信しました。');
          goto('login');

        } catch (e) {
          await logError(
            location.pathname,
            'パスワードリセット用のメールの送信に失敗しました。',
            e
          );

          if (e instanceof ApiClientError) {
            setApiClientError(e);
            return;
          }

          goto('internalServerError');
        }
      })();
    });
  };

  return (
    <>
      <form className="bg-primary flex flex-col min-w-full min-h-full justify-between py-12 px-16">
        <div>
          <div className="text-2xl text-white font-bold">パスワードをお忘れの場合</div>
          <hr className="w-8 border-4 border-white mt-2" />
          <div className="mt-1 text-danger-secondary text-xs">
            { apiClientError?.message }
            { (Object.keys(errors).length > 0) && '入力エラーがあります' }
          </div>
        </div>

        <div>
          <div className="text-white text-sm font-semibold mb-8">
            パスワード再設定用のメールを送信します。登録メールアドレスを入力してください。
          </div>

          { /* メールアドレス */ }
          <div className="mb-4">
            <label htmlFor="email">
              <div className="flex items-center">
                <span className="w-3 mr-1">
                  <img src={emailIcon} className="object-contain" alt="icon" />
                </span>
                <input
                  /* eslint-disable-next-line react/jsx-props-no-spreading */
                  {...register("email",
                    makeRegisterOptions('メールアドレス', [
                      { type: FormValidationType.REQUIRED },
                      { type: FormValidationType.MAX_STRING_LENGTH, value: 86 },
                      { type: FormValidationType.EMAIL, typeName: 'email' },
                    ])
                  )}
                  id="email"
                  className="flex-grow bg-transparent text-white text-sm border-b-2 border-white placeholder-white placeholder-opacity-40 focus:outline-none p-2"
                  type="text"
                  maxLength={86}
                  autoComplete="email"
                  placeholder="メールアドレス"
                />
              </div>
              <div className="ml-6">
                <div className="mt-1 text-danger-secondary text-xs">
                  {errors.email?.message}
                </div>
                <ValidationErrorMessages
                  {...{
                    fieldErrors: apiClientError?.errors,
                    field: 'email',
                    textColorClass: 'text-danger-secondary',
                  }}
                />
              </div>
            </label>
          </div>
        </div>

        <div className="flex justify-center">
          <button
            className="btn-primary text-6xl border-2 border-white rounded"
            type="button"
            onClick={handleSend}
          >
            送信
          </button>
        </div>
      </form>
    </>
  );
};
